// @ts-nocheck
import type { Dayjs } from "dayjs";
import type { TextFieldProps } from "@mui/material/TextField";
import type { DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import type { MobileDateTimePickerProps } from "@mui/x-date-pickers/MobileDateTimePicker";
import type { DateTimePickerProps } from "@mui/x-date-pickers/DateTimePicker";

import dayjs, { TIME_ZONE } from "@onair/utils/dayjs";
import { Controller, useFormContext } from "react-hook-form";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";

import { formatStr } from "#src/utils/format-time";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker, TimePickerProps } from "@mui/x-date-pickers/TimePicker";

// ----------------------------------------------------------------------

type RHFTimePickerProps = TimePickerProps<Dayjs> & {
  name: string;
};

export function RHFTimePicker({
  name,
  slotProps,
  ...other
}: RHFTimePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TimePicker
          {...field}
          value={dayjs.utc(field.value)}
          onChange={(newValue) => field.onChange(dayjs.utc(newValue).format())}
          format="HH:mm"
          timezone={TIME_ZONE}
          ampm={false}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              fullWidth: true,
              error: !!error,
              helperText:
                error?.message ??
                (slotProps?.textField as TextFieldProps)?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFDateTimePickerProps = DateTimePickerProps<Dayjs> & {
  name: string;
};

export function RHFDateTimePicker({
  name,
  slotProps,
  ...other
}: RHFDateTimePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <DateTimePicker
          {...field}
          value={field.value ? dayjs.utc(field.value) : null}
          onChange={(newValue) => field.onChange(dayjs.utc(newValue).format())}
          format="DD/MM/YYYY HH:mm"
          dayOfWeekFormatter={(v) => v.format("ddd")}
          timezone={TIME_ZONE}
          ampm={false}
          slotProps={{
            ...slotProps,
            textField: {
              fullWidth: true,
              ...slotProps?.textField,
              error: !!error,
              helperText:
                error?.message ??
                (slotProps?.textField as TextFieldProps)?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFDatePickerProps = DatePickerProps<Dayjs> & {
  name: string;
  error?: boolean;
  helperText?: string;
};

export function RHFDatePicker({
  name,
  slotProps,
  ...other
}: RHFDatePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          {...field}
          value={dayjs.utc(field.value)}
          onChange={(newValue) => field.onChange(dayjs.utc(newValue).format())}
          format="DD/MM/YYYY"
          dayOfWeekFormatter={(v) => v.format("ddd")}
          timezone={TIME_ZONE}
          slotProps={{
            ...slotProps,
            textField: {
              fullWidth: true,
              ...slotProps?.textField,
              error: !!error,
              helperText:
                error?.message ??
                (slotProps?.textField as TextFieldProps)?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMobileDateTimePickerProps = MobileDateTimePickerProps<Dayjs> & {
  name: string;
};

export function RHFMobileDateTimePicker({
  name,
  slotProps,
  ...other
}: RHFMobileDateTimePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <MobileDateTimePicker
          {...field}
          value={dayjs.utc(field.value)}
          onChange={(newValue) => field.onChange(dayjs.utc(newValue).format())}
          format="DD/MM/YYYY HH:mm"
          dayOfWeekFormatter={(v) => v.format("ddd")}
          timezone={TIME_ZONE}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              fullWidth: true,
              error: !!error,
              helperText:
                error?.message ??
                (slotProps?.textField as TextFieldProps)?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}
